import { Dialog as HeadlessDialog, Transition } from "@headlessui/react";
import { PhotoIcon, XMarkIcon } from "@heroicons/react/24/outline";
import React, { useState } from "react";

import { Paragraph } from "@fronterahealth/frontera-ui-components";

export interface ImagePreview {
  url: string;
  title: string;
}

interface ImagePreviewModalProps {
  preview: ImagePreview | null;
  onClose: () => void;
}

export const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({ preview, onClose }) => {
  const [loadFailed, setLoadFailed] = useState<boolean>(false);

  // Reset the error state every time a new image is opened.
  React.useEffect(() => {
    if (preview) setLoadFailed(false);
  }, [preview]);

  return (
    <Transition appear show={!!preview} as={React.Fragment}>
      <HeadlessDialog as="div" className="relative z-50" onClose={onClose}>
        {/* Backdrop */}
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/80" aria-hidden="true" />
        </Transition.Child>

        {/* Centered image */}
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-150"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <HeadlessDialog.Panel className="relative flex flex-col max-w-[90vw] max-h-[90vh] outline-none">
              <button
                type="button"
                onClick={onClose}
                aria-label="Close image preview"
                className="absolute -top-3 -right-3 p-1 rounded-full bg-white shadow hover:bg-limestone-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400 z-10"
              >
                <XMarkIcon className="h-5 w-5 text-text-primary" />
              </button>
              {preview && !loadFailed ? (
                <img
                  src={preview.url}
                  alt={preview.title}
                  className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl bg-white"
                  onError={() => {
                    console.error("[ImagePreviewModal] Image failed to load:", preview.url);
                    setLoadFailed(true);
                  }}
                />
              ) : preview ? (
                <div className="flex flex-col items-center gap-3 p-8 bg-white rounded-lg shadow-2xl">
                  <PhotoIcon className="h-12 w-12 text-limestone-400" />
                  <Paragraph size="small" colorType="primary">
                    Couldn't load this image.
                  </Paragraph>
                  <a
                    href={preview.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-teal-700 hover:underline"
                  >
                    Download Image
                  </a>
                </div>
              ) : null}
            </HeadlessDialog.Panel>
          </Transition.Child>
        </div>
      </HeadlessDialog>
    </Transition>
  );
};
